import { useEffect, useState } from "react";
import { MessageCircle, X, Send, Heart, Bug, Lightbulb, MoreHorizontal, Check } from "lucide-react";
import { client } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

const KINDS = [
  { id: "love", label: "Love it", icon: Heart },
  { id: "idea", label: "Idea / Request", icon: Lightbulb },
  { id: "bug", label: "Bug", icon: Bug },
  { id: "other", label: "Other", icon: MoreHorizontal },
];

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("love");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Auto-reset after closing
  useEffect(() => {
    if (!open && sent) {
      const t = setTimeout(() => {
        setSent(false);
        setMessage("");
        setEmail("");
        setKind("love");
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open, sent]);

  const submit = async () => {
    if (sending || message.trim().length < 3) return;
    setSending(true);
    try {
      await client.post("/feedback", {
        kind,
        message: message.trim(),
        email: email.trim(),
        page: typeof window !== "undefined" ? window.location.pathname : "",
      });
      track(EVENTS.FEEDBACK_SUBMITTED, { kind, length: message.trim().length });
      setSent(true);
    } catch {
      // soft fail — keep modal open for retry
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        className="fixed z-[60] rounded-full flex items-center gap-2 px-3.5 py-2.5 transition hover:opacity-90 shadow-lg"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
          right: "calc(env(safe-area-inset-right, 0px) + 16px)",
          background: "var(--ink)",
          color: "var(--bg)",
          zIndex: 60,
          boxShadow: "0 12px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(216,168,87,0.20)",
        }}
        aria-label="Send feedback"
      >
        <MessageCircle size={14} strokeWidth={1.8} />
        <span className="text-[12px] font-medium tracking-tight hidden sm:inline">Feedback</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[110] flex items-end md:items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="feedback-modal-backdrop"
          style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="feedback-modal"
            className="w-full sm:max-w-md md:max-w-md rounded-t-2xl md:rounded-2xl overflow-hidden max-h-[90vh] flex flex-col"
            style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
          >
            <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: "1px solid var(--ink-22)" }}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="seal-dot flex-shrink-0" />
                <h3 className="font-heading text-base sm:text-lg truncate" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
                  30 seconds — <span className="font-display-italic">tell us</span>.
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="feedback-close"
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 -mr-2"
                style={{ color: "var(--ink-72)" }}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {sent ? (
              <div className="p-8 text-center" data-testid="feedback-success">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto" style={{ background: "rgba(216,168,87,0.18)", border: "1px solid rgba(216,168,87,0.40)" }}>
                  <Check size={18} style={{ color: SEAL }} />
                </div>
                <h4 className="font-heading text-xl mt-4" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>
                  Got it. <span className="font-display-italic">Thank you.</span>
                </h4>
                <p className="text-sm mt-2" style={{ color: INK_72 }}>
                  Pradeep reads every message personally — usually replies within a day.
                </p>
                <button onClick={() => setOpen(false)} className="btn-secondary mt-6" data-testid="feedback-done">
                  Close
                </button>
              </div>
            ) : (
              <>
                <div className="p-5 space-y-4 overflow-y-auto">
                  <div>
                    <label className="eyebrow block mb-2">What is it?</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                      {KINDS.map(({ id, label, icon: Icon }) => (
                        <button
                          key={id}
                          onClick={() => setKind(id)}
                          data-testid={`feedback-kind-${id}`}
                          className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-lg transition text-[11px]"
                          style={{
                            background: kind === id ? "rgba(216,168,87,0.10)" : "transparent",
                            border: kind === id ? "1px solid var(--seal)" : "1px solid var(--ink-22)",
                            color: kind === id ? SEAL : INK_55,
                          }}
                        >
                          <Icon size={14} strokeWidth={1.7} />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="eyebrow block mb-2">Your message</label>
                    <textarea
                      data-testid="feedback-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell me what sucked, what you loved, or what's missing…"
                      rows={5}
                      className="input"
                      style={{ resize: "vertical", minHeight: 110, fontFamily: "'Inter', sans-serif" }}
                      maxLength={4000}
                    />
                    <div className="text-[10px] text-right mt-1" style={{ color: INK_55 }}>
                      {message.trim().length} / 4000
                    </div>
                  </div>

                  <div>
                    <label className="eyebrow block mb-2">Email <span className="normal-case tracking-normal" style={{ color: "var(--ink-38)" }}>(optional · so I can reply)</span></label>
                    <input
                      data-testid="feedback-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="input"
                      type="email"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 px-5 py-4" style={{ borderTop: "1px solid var(--ink-22)" }}>
                  <span className="text-[11px]" style={{ color: INK_55 }}>
                    Sent straight to the founder. No bots.
                  </span>
                  <button
                    onClick={submit}
                    disabled={sending || message.trim().length < 3}
                    className="btn-primary disabled:opacity-50"
                    data-testid="feedback-submit"
                  >
                    <Send size={13} />
                    {sending ? "Sending…" : "Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
